import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, Database, Users, FileText, Calendar, MessageSquare, Settings } from 'lucide-react';

interface DataSummary {
  tableName: string;
  totalRecords: number;
  tenantRecords: number;
  otherTenants: number;
  sampleData: any[];
}

interface TenantData {
  tenant_id: string;
  tenant_name?: string;
  record_count: number;
}

export default function RLSTestPage() {
  const [loading, setLoading] = useState(false);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);
  const [showAllData, setShowAllData] = useState(false);
  const [dataSummary, setDataSummary] = useState<DataSummary[]>([]);
  const [tenantData, setTenantData] = useState<TenantData[]>([]);
  const [error, setError] = useState<string | null>(null);

  // List of all tables that use tenant filtering
  const tenantFilteredTables = [
    // Core Business Tables
    { name: 'projects', displayName: 'Projects', icon: Database },
    { name: 'users', displayName: 'Users', icon: Users },
    { name: 'teams', displayName: 'Teams', icon: Users },
    { name: 'pipelines', displayName: 'Pipelines', icon: Database },
    { name: 'stages', displayName: 'Stages', icon: Database },
    
    // Project Related
    { name: 'project_followers', displayName: 'Project Followers', icon: Users },
    { name: 'project_contacts', displayName: 'Project Contacts', icon: Users },
    { name: 'project_activities', displayName: 'Project Activities', icon: FileText },
    { name: 'project_messages', displayName: 'Project Messages', icon: MessageSquare },
    { name: 'project_designs', displayName: 'Project Designs', icon: FileText },
    { name: 'project_finance_details', displayName: 'Project Finance Details', icon: FileText },
    { name: 'project_system_details', displayName: 'Project System Details', icon: FileText },
    { name: 'project_teams', displayName: 'Project Teams', icon: Users },
    
    // Task & Checklist Management
    { name: 'tasks', displayName: 'Tasks', icon: FileText },
    { name: 'checklist_groups', displayName: 'Checklist Groups', icon: FileText },
    { name: 'checklist_items', displayName: 'Checklist Items', icon: FileText },
    { name: 'checklist_templates', displayName: 'Checklist Templates', icon: FileText },
    { name: 'project_checklist_items', displayName: 'Project Checklist Items', icon: FileText },
    
    // Support & Communication
    { name: 'tickets', displayName: 'Tickets', icon: MessageSquare },
    { name: 'ticket_comments', displayName: 'Ticket Comments', icon: MessageSquare },
    { name: 'comments', displayName: 'Comments', icon: MessageSquare },
    { name: 'ticket_attachments', displayName: 'Ticket Attachments', icon: FileText },
    { name: 'ticket_history', displayName: 'Ticket History', icon: FileText },
    
    // Notifications
    { name: 'notifications', displayName: 'Notifications', icon: MessageSquare },
    { name: 'notification_preferences', displayName: 'Notification Preferences', icon: Settings },
    { name: 'notification_templates', displayName: 'Notification Templates', icon: FileText },
    { name: 'notification_channels', displayName: 'Notification Channels', icon: Settings },
    
    // Booking & Calendar
    { name: 'bookings', displayName: 'Bookings', icon: Calendar },
    { name: 'booking_links', displayName: 'Booking Links', icon: Calendar },
    { name: 'booking_requests', displayName: 'Booking Requests', icon: Calendar },
    { name: 'events', displayName: 'Events', icon: Calendar },
    { name: 'appointments', displayName: 'Appointments', icon: Calendar },
    
    // Documents & Files
    { name: 'documents', displayName: 'Documents', icon: FileText },
    { name: 'contracts', displayName: 'Contracts', icon: FileText },
    { name: 'document_extractions', displayName: 'Document Extractions', icon: FileText },
    
    // Activity & Audit
    { name: 'activity_logs', displayName: 'Activity Logs', icon: FileText },
    { name: 'rls_check_logs', displayName: 'RLS Check Logs', icon: FileText },
    
    // Automation & Webhooks
    { name: 'automations', displayName: 'Automations', icon: Settings },
    { name: 'automation_executions', displayName: 'Automation Executions', icon: Settings },
    { name: 'project_webhooks', displayName: 'Project Webhooks', icon: Settings },
    { name: 'webhook_queue', displayName: 'Webhook Queue', icon: Settings },
    
    // Financial
    { name: 'payments', displayName: 'Payments', icon: FileText },
    { name: 'financiers', displayName: 'Financiers', icon: FileText },
    { name: 'financier_states', displayName: 'Financier States', icon: FileText },
    
    // Installer Partners
    { name: 'installer_partners', displayName: 'Installer Partners', icon: Users },
    { name: 'installer_adder_cost', displayName: 'Installer Adder Cost', icon: FileText },
    
    // Location & Tracking
    { name: 'sales_rep_locations', displayName: 'Sales Rep Locations', icon: Database },
    { name: 'site_surveyor_locations', displayName: 'Site Surveyor Locations', icon: Database },
    
    // Theme & Settings
    { name: 'theme_settings', displayName: 'Theme Settings', icon: Settings },
    { name: 'tenant_theme_settings', displayName: 'Tenant Theme Settings', icon: Settings },
    { name: 'templates', displayName: 'Templates', icon: FileText },
    
    // User Preferences
    { name: 'user_pipeline_preferences', displayName: 'User Pipeline Preferences', icon: Settings },
    { name: 'search_history', displayName: 'Search History', icon: FileText },
    
    // Design & Engineering
    { name: 'design_requests', displayName: 'Design Requests', icon: FileText },
    { name: 'redlines', displayName: 'Redlines', icon: FileText },
    { name: 'thryve_cases', displayName: 'Thryve Cases', icon: FileText },
    
    // Property & Site Data
    { name: 'property_data', displayName: 'Property Data', icon: Database },
    { name: 'adders', displayName: 'Adders', icon: FileText },
    
    // Communication & Read Status
    { name: 'comment_read_status', displayName: 'Comment Read Status', icon: MessageSquare },
    { name: 'message_read_status', displayName: 'Message Read Status', icon: MessageSquare },
    { name: 'message_reactions', displayName: 'Message Reactions', icon: MessageSquare },
    
    // PIN/Field App
    { name: 'pins', displayName: 'Pins', icon: Database },
    { name: 'pin_activities', displayName: 'Pin Activities', icon: FileText },
    { name: 'pin_contacts', displayName: 'Pin Contacts', icon: Users },
    { name: 'pin_documents', displayName: 'Pin Documents', icon: FileText },
    { name: 'pin_notes', displayName: 'Pin Notes', icon: FileText },
    
    // Security & Session
    { name: 'biometric_sessions', displayName: 'Biometric Sessions', icon: Settings },
    { name: 'password_reset_tokens', displayName: 'Password Reset Tokens', icon: Settings },
    { name: 'service_account_keys', displayName: 'Service Account Keys', icon: Settings },
    
    // Work Orders & Operations
    { name: 'work_orders', displayName: 'Work Orders', icon: FileText },
    { name: 'site_survey_requests', displayName: 'Site Survey Requests', icon: FileText },
    
    // Other Core Tables
    { name: 'profiles', displayName: 'Profiles', icon: Users },
    { name: 'contacts', displayName: 'Contacts', icon: Users },
    { name: 'articles', displayName: 'Articles', icon: FileText },
    { name: 'daily_debriefs', displayName: 'Daily Debriefs', icon: FileText },
    
    // Additional tables from services
    { name: 'leads', displayName: 'Leads', icon: Users },
    { name: 'tracking', displayName: 'Tracking', icon: Database },
    { name: 'booking_reminders', displayName: 'Booking Reminders', icon: Calendar },
    { name: 'slack_integrations', displayName: 'Slack Integrations', icon: Settings },
    { name: 'videos', displayName: 'Videos', icon: FileText },
    { name: 'playlists', displayName: 'Playlists', icon: FileText },
    { name: 'video_progress', displayName: 'Video Progress', icon: FileText },
  ];

  useEffect(() => {
    getCurrentTenantId();
  }, []);

  const getCurrentTenantId = async () => {
    try {
      const { data, error } = await supabase.rpc('get_current_user_tenant_id');
      if (error) throw error;
      setCurrentTenantId(data);
    } catch (err) {
      console.error('Error getting tenant ID:', err);
      setError('Failed to get current tenant ID');
    }
  };

  const fetchDataSummary = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const summary: DataSummary[] = [];
      
      for (const table of tenantFilteredTables) {
        try {
          // Get total count
          const { count: totalCount } = await supabase
            .from(table.name)
            .select('*', { count: 'exact', head: true });
          
          // Get count for current tenant
          const { count: tenantCount } = await supabase
            .from(table.name)
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', currentTenantId);
          
          // Get sample data (first 5 records)
          const { data: sampleData } = await supabase
            .from(table.name)
            .select('*')
            .limit(5);
          
          summary.push({
            tableName: table.displayName,
            totalRecords: totalCount || 0,
            tenantRecords: tenantCount || 0,
            otherTenants: (totalCount || 0) - (tenantCount || 0),
            sampleData: sampleData || []
          });
        } catch (err) {
          console.warn(`Error fetching data for ${table.name}:`, err);
          summary.push({
            tableName: table.displayName,
            totalRecords: 0,
            tenantRecords: 0,
            otherTenants: 0,
            sampleData: []
          });
        }
      }
      
      setDataSummary(summary);
    } catch (err) {
      console.error('Error fetching data summary:', err);
      setError('Failed to fetch data summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantBreakdown = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const tenantData: TenantData[] = [];
      
      // Get unique tenant IDs from projects table
      const { data: tenantIds } = await supabase
        .from('projects')
        .select('tenant_id')
        .not('tenant_id', 'is', null);
      
      if (tenantIds) {
        const uniqueTenantIds = [...new Set(tenantIds.map(t => t.tenant_id))];
        
        for (const tenantId of uniqueTenantIds) {
          const { count } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenantId);
          
          tenantData.push({
            tenant_id: tenantId,
            record_count: count || 0
          });
        }
      }
      
      setTenantData(tenantData);
    } catch (err) {
      console.error('Error fetching tenant breakdown:', err);
      setError('Failed to fetch tenant breakdown');
    } finally {
      setLoading(false);
    }
  };

  const renderDataTable = (data: any[], tableName: string) => {
    if (!data || data.length === 0) {
      return <p className="text-muted-foreground">No data available</p>;
    }

    const columns = Object.keys(data[0]).filter(key => 
      !key.includes('password') && 
      !key.includes('token') && 
      !key.includes('secret') &&
      key !== 'id' // Skip ID for privacy
    );

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(column => (
                <TableHead key={column}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                {columns.map(column => (
                  <TableCell key={column}>
                    {typeof row[column] === 'object' 
                      ? JSON.stringify(row[column]).substring(0, 50) + '...'
                      : String(row[column] || '').substring(0, 100)
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">RLS Policy Test Page</h1>
          <p className="text-muted-foreground">
            Hidden page for testing Row Level Security policies and tenant data isolation
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowAllData(!showAllData)}
          >
            {showAllData ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showAllData ? 'Hide Sample Data' : 'Show Sample Data'}
          </Button>
          <Button onClick={fetchDataSummary} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Refresh Data
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Current Tenant Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Current Tenant ID:</label>
              <p className="text-sm text-muted-foreground font-mono">{currentTenantId || 'Loading...'}</p>
            </div>
            <div>
              <Button onClick={fetchTenantBreakdown} disabled={loading} variant="outline">
                View Tenant Breakdown
              </Button>
            </div>
          </div>
          
          {tenantData.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Tenant Breakdown (Projects):</h4>
              <div className="space-y-2">
                {tenantData.map(tenant => (
                  <div key={tenant.tenant_id} className="flex justify-between items-center p-2 bg-muted rounded">
                    <span className="font-mono text-sm">{tenant.tenant_id}</span>
                    <Badge variant={tenant.tenant_id === currentTenantId ? "default" : "secondary"}>
                      {tenant.record_count} projects
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList>
          <TabsTrigger value="summary">Data Summary</TabsTrigger>
          <TabsTrigger value="details">Detailed View</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dataSummary.map((summary, index) => {
              const Icon = tenantFilteredTables[index]?.icon || Database;
              return (
                <Card key={summary.tableName}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <CardTitle className="text-sm">{summary.tableName}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Total Records:</span>
                        <Badge variant="outline">{summary.totalRecords}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Your Tenant:</span>
                        <Badge variant="default">{summary.tenantRecords}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Other Tenants:</span>
                        <Badge variant="secondary">{summary.otherTenants}</Badge>
                      </div>
                    </div>
                    
                    {showAllData && summary.sampleData.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h5 className="text-sm font-medium mb-2">Sample Data:</h5>
                        <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto">
                          {renderDataTable(summary.sampleData, summary.tableName)}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          {dataSummary.map((summary, index) => {
            const Icon = tenantFilteredTables[index]?.icon || Database;
            return (
              <Card key={summary.tableName}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    <CardTitle>{summary.tableName}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{summary.totalRecords}</div>
                      <div className="text-sm text-muted-foreground">Total Records</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{summary.tenantRecords}</div>
                      <div className="text-sm text-muted-foreground">Your Tenant</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">{summary.otherTenants}</div>
                      <div className="text-sm text-muted-foreground">Other Tenants</div>
                    </div>
                  </div>
                  
                  {showAllData && summary.sampleData.length > 0 && (
                    <div>
                      <h5 className="font-medium mb-2">Sample Data:</h5>
                      {renderDataTable(summary.sampleData, summary.tableName)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
